import { Auth0DecodedHash } from 'auth0-js';
import { changeLanguage } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { updateUserMetadata } from 'src/auth/auth0';
import { routes } from 'src/constants/routes';
import { useAddUserToBasket } from 'src/lib/hooks/use-post-add-user-to-basket';
import { ValidationErrors } from 'src/shared/enum/ValidationErrors';
import { BasketUser, setBasket, updateBasketUser } from 'src/store/slices/basket-slice';
import { setLanguage } from 'src/store/slices/language-slice';
import { setSnackbarState } from 'src/store/slices/snackbar-slice';
import { setDetails, setLoggedIn, setTokenDetails } from 'src/store/slices/user-slice';
import store from 'src/store/store';
import {
    getBasketStorage,
    getUserStorage,
    setBasketStorage,
    setLanguageStorage,
    setStorage,
    setUserStorage,
} from 'src/utils/local-storage-versioning';

import { METADATA_ATTR_URL } from '../auth/constants';
import { PathNames } from '../shared/enum/PathNames';

export const useHandleUserAuth = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mutateAsync: addUserToBasket } = useAddUserToBasket();
    const nonBasketRoutes = [PathNames.FAILURE, PathNames.SUCCESS, PathNames.PROCESSING];

    const handleUserAuth = async (authResult: Auth0DecodedHash) => {
        const currentBasket = getBasketStorage();
        if (authResult) {
            setStorage({
                auth0: {
                    access_token: authResult.accessToken || '',
                    id_token: authResult.idToken || '',
                    tokenExpired: false,
                },
            });
            store.dispatch(setTokenDetails(authResult));
            store.dispatch(setLoggedIn(true));

            const userId = authResult.idTokenPayload.sub.split('|')[1];
            const userAuth0Id = authResult.idTokenPayload.sub;
            const userMetadata = authResult.idTokenPayload[METADATA_ATTR_URL];
            setUserStorage({ ...userMetadata, email: authResult.idTokenPayload.name });

            const user = getUserStorage();

            if (user) {
                // Auth0 restricts metadata fields to 10 on create user method,
                // so we have to call update user metadata method when they return from Auth0 account creation
                user.address1 = user.address1 ?? user.address;
                user.address2 = user.address2 ?? '-';
                await updateUserMetadata(userAuth0Id, user);
                store.dispatch(setDetails(user));

                if (!nonBasketRoutes.includes(<PathNames>window.location.pathname)) {
                    // Date of birth needs to support old user metadata
                    const hasDateOfBirth =
                        userMetadata.dob || userMetadata.dateOfBirth || user.dateOfBirth;

                    let dob = null;

                    if (hasDateOfBirth) {
                        dob =
                            userMetadata.dob && !userMetadata.dateOfBirth
                                ? userMetadata.dob
                                : `${user.dateOfBirth.year}-${user.dateOfBirth.month?.padStart(2, '0')}-${user.dateOfBirth.day?.padStart(2, '0')}`;
                    }

                    const phoneNumber = user.phone ? user.phone.number : userMetadata.phoneNumber;
                    const phoneCode = user.phone ? user.phone.callingCode : userMetadata.phoneCode;

                    const basketUser: BasketUser = {
                        id: userId,
                        first_name: user.firstName,
                        last_name: user.lastName,
                        address1: user.address1,
                        address2: user.address2,
                        city: user.city,
                        country: user.country,
                        nationality: user.nationality,
                        number: phoneNumber,
                        phone_code: phoneCode,
                        post_code: user.postCode,
                        study_year: user.studyYear,
                        university: user.university,
                        dob,
                        email: user.email,
                        preferred_language: user.preferredLanguage,
                        is_spanish_resident: user.isSpanishResident === 'Yes',
                        nie_number: user.nieNumber ?? null,
                        nif_number: user.nifNumber ?? null,
                        passport_number: user.passportNumber ?? null,
                        passport_issuing_country: user.passportIssuingCountry ?? null,
                    };

                    store.dispatch(updateBasketUser(basketUser));

                    // Call addUserToBasket with user data from local storage
                    if (currentBasket && currentBasket.id && currentBasket.updated_date) {
                        try {
                            // Call add user to basket hook
                            const response = await addUserToBasket({
                                basketId: currentBasket.id,
                                userData: basketUser,
                                updatedAt: currentBasket.updated_date,
                            });

                            // Use response to update local storage and basket
                            setBasketStorage(response);
                            store.dispatch(setBasket(response));
                        } catch (error) {
                            if (
                                error.response.data &&
                                error.response.data.data &&
                                error.response.data.data.room
                            ) {
                                if (
                                    error.response.data.data.room[0] ===
                                    ValidationErrors.ROOM_NOT_AVAILABLE
                                ) {
                                    navigate(routes.roomNotAvailable.index);
                                    return;
                                }
                                if (
                                    error.response.data.data.room[0] ===
                                    ValidationErrors.BASE_PRICE_MISMATCH
                                ) {
                                    navigate(routes.summary.index);
                                    store.dispatch(
                                        setSnackbarState({
                                            message: t('room_price_updated_message'),
                                        })
                                    );
                                }
                            }
                        }
                    }
                }
            }

            const { preferredLanguage } = userMetadata;

            if (preferredLanguage) {
                changeLanguage(preferredLanguage);
                store.dispatch(setLanguage(preferredLanguage));
                setLanguageStorage(preferredLanguage);
            }
        }
    };

    return { handleUserAuth };
};
